import React, { useRef, useState, useEffect } from 'react';
import {
  Modal,
  View,
  StyleSheet,
  TouchableOpacity,
  Text,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { WebView } from 'react-native-webview';
import { SafeAreaView } from 'react-native-safe-area-context';

interface PaymentWebViewModalProps {
  visible: boolean;
  checkoutUrl: string;
  isEpayForm?: boolean; 
  onClose: () => void;
  onSuccess: (depositId: string) => void;
  onCancel: (depositId: string) => void;
}

export const PaymentWebViewModal: React.FC<PaymentWebViewModalProps> = ({
  visible,
  checkoutUrl,
  isEpayForm = false,
  onClose,
  onSuccess,
  onCancel,
}) => {
  const webViewRef = useRef<WebView>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('PaymentWebViewModal mounted with:', {
      isEpayForm,
      checkoutUrlLength: checkoutUrl?.length,
      checkoutUrlPreview: checkoutUrl?.substring(0, 100),
    });
  }, [checkoutUrl, isEpayForm]);

  // Helper function to extract query params (Hermes-compatible)
  const getQueryParam = (url: string, param: string): string | null => {
    try {
      const queryString = url.split('?')[1];
      if (!queryString) return null;
      
      const params = queryString.split('&');
      for (const p of params) {
        const [key, value] = p.split('=');
        if (key === param) {
          return decodeURIComponent(value);
        }
      }
      return null;
    } catch (error) {
      console.error('Error parsing query params:', error);
      return null;
    }
  };

  const handleNavigationStateChange = (navState: any) => {
    const { url } = navState;
    console.log('WebView navigated to:', url);

    // Check for success URL (both Stripe and ePay patterns)
    if (url.includes('payment=success') || url.includes('success')) {
      console.log('Payment successful!');
      setLoading(false);
      
      // Extract depositId using our Hermes-compatible function
      const depositId = getQueryParam(url, 'deposit_id') || 
                       getQueryParam(url, 'epayid') || 
                       '';
      
      onSuccess(depositId);
      return;
    } 
    
    if (url.includes('payment=cancelled') || url.includes('cancel')) {
      console.log('Payment cancelled!');
      setLoading(false);
      
      const depositId = getQueryParam(url, 'deposit_id') || 
                       getQueryParam(url, 'epayid') || 
                       '';
      
      onCancel(depositId);
      return;
    }
  };

  const handleWebViewError = (syntheticEvent: any) => {
    const { nativeEvent } = syntheticEvent;
    console.error('WebView error:', nativeEvent);
    setLoading(false);
  };

  const handleMessage = (event: any) => {
    console.log('WebView message:', event.nativeEvent.data);
  };

  // ✅ FIX: Determine the source for WebView based on isEpayForm
  const webViewSource = isEpayForm 
    ? { 
        html: checkoutUrl, 
        baseUrl: 'https://demo.epay.bg' 
      }
    : { uri: checkoutUrl };

  console.log('WebView source:', isEpayForm ? 'HTML' : 'URI', webViewSource);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.container} edges={['top']}>
        {/* Header with Close Button */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Complete Payment</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
          >
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        {/* Loading Indicator */}
        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#00f0ff" />
            <Text style={styles.loadingText}>
              {isEpayForm ? 'Loading ePay payment...' : 'Loading payment...'}
            </Text>
          </View>
        )}

        {/* WebView */}
        <WebView
          ref={webViewRef}
          source={webViewSource}
          onNavigationStateChange={handleNavigationStateChange}
          onLoadStart={() => {
            console.log('WebView load started');
            setLoading(true);
          }}
          onLoadEnd={() => {
            console.log('WebView load ended');
            setLoading(false);
          }}
          onError={handleWebViewError}
          onMessage={handleMessage}
          startInLoadingState={true}
          javaScriptEnabled={true}
          domStorageEnabled={true}
          thirdPartyCookiesEnabled={true}
          sharedCookiesEnabled={true}
          allowsInlineMediaPlayback={true}
          mediaPlaybackRequiresUserAction={false}
          originWhitelist={['*']}
          mixedContentMode="always"
          style={styles.webView}
        />
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#112345',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#00f0ff',
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 240, 255, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(0, 240, 255, 0.3)',
  },
  closeButtonText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#00f0ff',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 999,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#112345',
  },
  webView: {
    flex: 1,
  },
});